import { dashboardLocationColor } from '@/lib/dashboardColors'
// Mantine
import { Button } from '@mantine/core'

// Next
import { Link } from 'react-router-dom'
import RouterLink from '@/components/ui/RouterLink'

// Icons
import { IconCheck, IconClockCog, IconChevronRight } from '@tabler/icons-react'

// Utils
import { sumBy } from 'lodash'

// UI
import Box from '../ui/Box'

/**
 * Helper function for French pluralization
 */
const pluralize = (count, singular, plural = singular + 's') =>
    `${count} ${count > 1 ? plural : singular}`

/**
 * Dashboard component displaying production orders status across locations
 * @param {Array} production_orders - Array of production order data by location and status
 * @returns {JSX.Element} Production orders status widget
 */
export default function DashboardProductionOrders({ production_orders }) {
    // Simple calculations
    const openOrders =
        production_orders?.filter((order) => order.status === 'open') || []
    const openCount = sumBy(openOrders, 'count')
    const hasOpenOrders = openCount > 0

    // Group by location
    const locationMap = new Map()
    production_orders?.forEach((order) => {
        const locationKey = `${order.location_id}-${order.location_name}`
        if (!locationMap.has(locationKey)) {
            locationMap.set(locationKey, {
                id: order.location_id,
                name: order.location_name,
                orders: [],
            })
        }
        locationMap.get(locationKey).orders.push(order)
    })
    const locations = Array.from(locationMap.values())

    // Action button
    const actionButton = (
        <Button
            component={RouterLink}
            href="/assembly?status=open,scheduled"
            variant="default"
            size="xs"
            rightSection={<IconChevronRight size={14} />}
            className="text-xs font-medium"
        >
            Voir tout
        </Button>
    )

    return (
        <Box title="Assemblages" rightSection={actionButton}>
            <div className="space-y-6">
                {/* Hero metrics section */}
                {production_orders && (
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div
                                className={`p-3 rounded-xl border ${
                                    hasOpenOrders
                                        ? 'bg-blue-50 border-blue-100'
                                        : 'bg-green-50 border-green-100'
                                }`}
                            >
                                <IconClockCog
                                    size={20}
                                    className={
                                        hasOpenOrders
                                            ? 'text-blue-600'
                                            : 'text-green-600'
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-gray-900 tracking-tight">
                                    {openCount}
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-0.5">
                                {openCount === 0
                                    ? 'Aucun assemblage en cours'
                                    : `Assemblage${
                                          openCount > 1 ? 's' : ''
                                      } en cours`}
                            </div>
                        </div>
                    </div>
                )}

                {/* Perfect state - no orders */}
                {production_orders && openCount === 0 && (
                    <div className="text-center py-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-linear-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100 mb-4">
                            <IconCheck size={28} className="text-green-600" />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Aucun assemblage en cours
                            </h3>
                            <p className="text-sm text-gray-600 max-w-sm mx-auto">
                                Tous les assemblages sont terminés.
                            </p>
                        </div>
                    </div>
                )}

                {/* Loading placeholder — uses same markup as real content with invisible text */}
                {!production_orders && (
                    <>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className="p-3 rounded-xl border border-gray-200 animate-pulse">
                                    <IconClockCog size={20} className="text-gray-200" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl font-bold text-transparent bg-gray-200 rounded-sm animate-pulse">00</span>
                                </div>
                                <div className="text-sm text-transparent bg-gray-100 rounded-sm animate-pulse mt-0.5">Assemblages en cours</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-medium text-transparent uppercase tracking-wider mb-3">
                                <span className="bg-gray-200 rounded-sm animate-pulse">Par atelier</span>
                            </div>
                            {[1, 2].map((i) => (
                                <div
                                    key={i}
                                    className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs"
                                >
                                    <div className="p-5 pb-3">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-medium text-transparent text-base bg-gray-200 rounded-sm animate-pulse">Nom atelier</h3>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="px-3 py-1.5 rounded-full text-xs font-medium text-transparent bg-gray-200 border border-gray-200 animate-pulse">2 assemblages</div>
                                                <IconChevronRight size={16} className="text-gray-200" />
                                            </div>
                                        </div>

                                        <div className="flex gap-3">
                                            <div className="flex-1 bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                                                <div className="flex justify-between items-start mb-1">
                                                    <div className="text-xs font-medium text-transparent bg-gray-200 rounded-sm animate-pulse">En cours</div>
                                                    <div className="text-xs px-1.5 py-0.5 text-transparent bg-gray-200 rounded-sm animate-pulse">0</div>
                                                </div>
                                                <div className="text-sm text-transparent bg-gray-100 rounded-sm animate-pulse">00 montres à assembler</div>
                                                <div className="text-sm text-transparent bg-gray-100 rounded-sm animate-pulse">00 en contrôle</div>
                                            </div>
                                            <div className="flex-1 bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                                                <div className="flex justify-between items-start mb-1">
                                                    <div className="text-xs font-medium text-transparent bg-gray-200 rounded-sm animate-pulse">Programmés</div>
                                                    <div className="text-xs px-1.5 py-0.5 text-transparent bg-gray-200 rounded-sm animate-pulse">0</div>
                                                </div>
                                                <div className="text-sm text-transparent bg-gray-100 rounded-sm animate-pulse">00 montres programmées</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Location cards */}
                {locations.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                            Par atelier
                        </div>
                        {locations.map((location, index) => {
                            // Calculate metrics for each status
                            const openOrders = location.orders.filter(
                                (order) => order.status === 'open'
                            )
                            const scheduledOrders = location.orders.filter(
                                (order) => order.status === 'scheduled'
                            )

                            const metrics = {
                                open: {
                                    count: sumBy(openOrders, 'count'),
                                    quantity:
                                        sumBy(openOrders, 'quantity') -
                                        sumBy(openOrders, 'quantity_completed'),
                                    quantity_in_control: sumBy(
                                        openOrders,
                                        'quantity_in_control'
                                    ),
                                },
                                scheduled: {
                                    count: sumBy(scheduledOrders, 'count'),
                                    quantity: sumBy(
                                        scheduledOrders,
                                        'quantity'
                                    ),
                                },
                            }

                            const totalOrdersCount =
                                metrics.open.count + metrics.scheduled.count

                            return (
                                <Link
                                    key={index}
                                    to={`/assembly?location_id=${location.id}&status=open,scheduled`}
                                    className="group block"
                                >
                                    <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-xs">
                                        {/* Header */}
                                        <div className="p-5 pb-3">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="flex items-center gap-2 font-medium text-gray-900 text-base transition-colors">
                                                        <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: dashboardLocationColor(location.id) }} aria-hidden="true" />
                                                        {location.name}
                                                    </h3>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <div className="px-3 py-1.5 rounded-full text-xs font-medium border" style={{ color: dashboardLocationColor(location.id), backgroundColor: `${dashboardLocationColor(location.id)}12`, borderColor: `${dashboardLocationColor(location.id)}33` }}>
                                                        {pluralize(
                                                            totalOrdersCount,
                                                            'assemblage'
                                                        )}
                                                    </div>

                                                    <IconChevronRight
                                                        size={16}
                                                        className="text-gray-400 group-hover:translate-x-1 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Metrics */}
                                            <div className="flex gap-3">
                                                {/* Open Orders */}
                                                {metrics.open.count > 0 && (
                                                    <div className="flex-1 bg-gray-50 group-hover:bg-gray-100 rounded-lg p-2.5 border border-gray-200 relative">
                                                        <div className="flex justify-between items-start mb-1">
                                                            <div className="text-gray-700 text-xs font-medium">
                                                                En cours
                                                            </div>

                                                            <div className="text-blue-700 text-xs px-1.5 py-0.5 bg-blue-100 rounded-sm">
                                                                {
                                                                    metrics.open
                                                                        .count
                                                                }
                                                            </div>
                                                        </div>

                                                        <div className="text-gray-500 text-sm">
                                                            {`${metrics.open.quantity} montres à assembler`}
                                                        </div>

                                                        <div className="text-gray-500 text-sm">
                                                            {`${metrics.open.quantity_in_control} en contrôle`}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Scheduled Orders */}
                                                {metrics.scheduled.count >
                                                    0 && (
                                                    <div className="flex-1 bg-gray-50 group-hover:bg-gray-100 rounded-lg p-2.5 border border-gray-200 relative">
                                                        <div className="flex justify-between items-start mb-1">
                                                            <div className="text-gray-700 text-xs font-medium">
                                                                Programmés
                                                            </div>
                                                            <div className="text-amber-700 text-xs px-1.5 py-0.5 bg-amber-100 rounded-sm">
                                                                {
                                                                    metrics
                                                                        .scheduled
                                                                        .count
                                                                }
                                                            </div>
                                                        </div>

                                                        <div className="text-gray-500 text-sm">
                                                            {
                                                                metrics
                                                                    .scheduled
                                                                    .quantity
                                                            }{' '}
                                                            montres programmées
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Subtle gradient overlay on hover */}
                                        <div className="absolute inset-0 bg-linear-to-r from-blue-50/0 via-blue-50/0 to-blue-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                                    </div>
                                </Link>
                            )
                        })}
                    </div>
                )}
            </div>
        </Box>
    )
}
